import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Button, Modal } from 'antd';
import { useSelector } from 'react-redux';
import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';
import useConfig from '@/Components/Hooks/useConfig';
import './IntendedUseStatementModal.less';

const MUSTACHE_PLACEHOLDER_REGEX = /\{\{\s*\$([a-zA-Z0-9_]+)\s*\}\}/g;

const renderMustacheTemplate = (template = '', values = {}) => {
  if (typeof template !== 'string' || !template) return '';
  return template.replace(MUSTACHE_PLACEHOLDER_REGEX, (_, key) => {
    const value = values[key];
    return value === undefined || value === null ? '' : String(value);
  });
};

const pickFirstString = (source = {}, keys = [], fallback = '') => {
  const value = keys.map((key) => source?.[key]).find((item) => typeof item === 'string');
  return value || fallback;
};

const IntendedUseStatementModal = ({ open, onProceed, useruuid }) => {
  const trans = useSelector(translator);
  const { data: currentUserConfig = {} } = useConfig({ useruuid });

  const intendedUseConfig = useMemo(
    () =>
      currentUserConfig?.intended_usage_config || currentUserConfig?.intented_usage_config || {},
    [currentUserConfig]
  );

  const templateVariables = useMemo(
    () => ({
      product_name: pickFirstString(intendedUseConfig, ['product_name'], 'HomeSight'),
      product_name_full: pickFirstString(
        intendedUseConfig,
        ['product_name_full'],
        'HomeSight Wellness Module'
      ),
      principal_user: pickFirstString(intendedUseConfig, ['principal_user'], 'resident'),
      legal_name: pickFirstString(intendedUseConfig, ['legal_name'], 'FDA'),
    }),
    [intendedUseConfig]
  );

  const statementTitle = useMemo(() => {
    const titleTemplate = pickFirstString(intendedUseConfig, ['title', 'statement_title']);
    return (
      renderMustacheTemplate(titleTemplate, templateVariables) ||
      renderMustacheTemplate(trans('INTENDED_USE_STATEMENT_TITLE'), templateVariables)
    );
  }, [intendedUseConfig, templateVariables, trans]);

  const statementBody = useMemo(() => {
    const bodyTemplate = pickFirstString(intendedUseConfig, ['body', 'statement_body']);
    const rendered =
      renderMustacheTemplate(bodyTemplate, templateVariables) ||
      renderMustacheTemplate(trans('INTENDED_USE_STATEMENT_BODY'), templateVariables);
    return rendered
      .split(/\n{2,}/)
      .map((paragraph) => paragraph.trim())
      .filter(Boolean);
  }, [intendedUseConfig, templateVariables, trans]);

  const proceedLabel = useMemo(
    () => trans('INTENDED_USE_STATEMENT_PROCEED'),
    [trans]
  );

  return (
    <Modal
      open={open}
      centered
      closable={false}
      maskClosable={false}
      keyboard={false}
      footer={null}
      width={1040}
      wrapClassName="intended-use-modal"
      destroyOnClose
    >
      <div className="intended-use-modal-content">
        <h2 className="intended-use-modal-title">{statementTitle}</h2>
        {statementBody.map((paragraph, index) => (
          <p key={index} className="intended-use-modal-paragraph">
            {paragraph}
          </p>
        ))}

        <Button
          type="link"
          className="intended-use-modal-proceed"
          onClick={onProceed}
        >
          {`[ ${proceedLabel} ]`}
        </Button>
      </div>
    </Modal>
  );
};

IntendedUseStatementModal.propTypes = {
  open: PropTypes.bool,
  onProceed: PropTypes.func,
  useruuid: PropTypes.string,
};

export default IntendedUseStatementModal;
